import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';
import LoadingRing from '../../ui-components/LoadingRing';
import cptActivityService, {
    MonthlyRow,
} from '../../../services/api/CPTAnalytics/CPTActivityService';
import CPTMonthlyTable from './CPTMonthlyTable';
import { toDateInputValue } from '../../../utils/dateUtils';

const getMonthlyRange = (page: number) => {
    const now = new Date();
    const endDate = page === 0
        ? now
        : new Date(now.getFullYear(), now.getMonth() - page * 12, 0);
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1);
    return { startDate, endDate };
};

const formatRangeLabel = (startDate: Date, endDate: Date): string => {
    const fmt = (d: Date) =>
        d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return `${fmt(startDate)} — ${fmt(endDate)}`;
};

export default function CPTMonthlyOverviewSection() {
    const [monthlyPage, setMonthlyPage] = useState(0);
    const [monthlyRows, setMonthlyRows] = useState<MonthlyRow[]>([]);
    const [loading, setLoading] = useState(true);

    const { startDate, endDate } = getMonthlyRange(monthlyPage);
    const rangeLabel = formatRangeLabel(startDate, endDate);

    useEffect(() => {
        const fetchMonthly = async () => {
            setLoading(true);
            try {
                const data = await cptActivityService.getMonthlyAnalytics({
                    start: toDateInputValue(startDate),
                    end: toDateInputValue(endDate),
                });
                setMonthlyRows(data);
            } catch {
                setMonthlyRows([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMonthly();
    }, [monthlyPage]);

    return (
        <section>
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">
                        Monthly Overview
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        CPT activity and revenue trends over the past 12 months.
                    </p>
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={loading}
                        onClick={() => setMonthlyPage((p) => p + 1)}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="min-w-[160px] text-center text-xs font-medium">
                        {loading ? '...' : rangeLabel}
                    </span>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={loading || monthlyPage === 0}
                        onClick={() => setMonthlyPage((p) => Math.max(0, p - 1))}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center py-8">
                    <LoadingRing size="large" />
                </div>
            ) : (
                <CPTMonthlyTable rows={monthlyRows} />
            )}
        </section>
    );
}
